import type { Route } from "./+types/search";
import { Link, Form } from "react-router";
import { content } from "../lib/content";
import { pageMeta } from "../lib/meta";
import { categoryAccent, categoryLabel } from "../utils/categoryAccent";
import { formatUtcShortDate } from "../utils/formatDate";
import { HERO_ART } from "../config/heroArt";

// Full-text search across the 500+ article library (title + description, via the
// content API's `q` param). SSR so results are shareable/crawlable; the page
// itself is noindex (query pages shouldn't pollute the index). This is the
// primary "I came from Google looking for one thing" entry point.
export async function loader({ request }: Route.LoaderArgs) {
  const q = (new URL(request.url).searchParams.get("q") || "").trim();
  if (!q) return { q: "", results: [], total: 0 };
  const list = await content.articles({ q, limit: 40 });
  return { q, results: list.items, total: list.total };
}

export function meta({ data }: Route.MetaArgs) {
  const q = data?.q;
  return [
    ...pageMeta({
      title: q ? `Search: ${q} | Gazar` : "Search | Gazar",
      description: "Search 500+ articles on system design, AI, algorithms, and engineering leadership.",
      path: "/search",
    }),
    { name: "robots", content: "noindex, follow" },
  ];
}

export default function Search({ loaderData }: Route.ComponentProps) {
  const { q, results, total } = loaderData;
  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="font-sans text-[40px] sm:text-[52px] font-bold text-brand-navy leading-[1.1] tracking-[-0.022em] mb-2">
        Search
      </h1>
      <p className="text-brand-navy/70 mb-6">Find anything across 500+ articles on system design, AI, algorithms, and leadership.</p>

      <Form method="get" role="search" className="flex gap-2">
        <input
          type="search"
          name="q"
          defaultValue={q}
          autoFocus
          placeholder="Search articles…"
          aria-label="Search articles"
          className="flex-1 rounded-lg border border-brand-navy/25 bg-white px-4 py-3 text-brand-navy text-[17px] focus:outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20"
        />
        <button
          type="submit"
          className="rounded-lg bg-brand-navy px-6 py-3 font-heading font-bold text-white transition-colors hover:bg-brand-navy/90"
        >
          Search
        </button>
      </Form>

      {q ? (
        <p className="mt-8 mb-2 font-heading text-overline uppercase tracking-[0.14em] text-brand-navy/45">
          {total} result{total === 1 ? "" : "s"} for &ldquo;{q}&rdquo;
        </p>
      ) : null}

      {q && results.length === 0 ? (
        <div className="mt-6 rounded-xl border border-brand-navy/12 bg-brand-navy/[0.03] p-8 text-center">
          {/* Decorative: the copy below carries the message. */}
          <img
            src={HERO_ART.searchempty[0]}
            alt=""
            aria-hidden="true"
            className="mx-auto mb-6 w-full max-w-[240px] rounded-xl"
            loading="lazy"
          />
          <p className="font-heading text-lg font-bold text-brand-navy">No matches for &ldquo;{q}&rdquo;</p>
          <p className="mt-2 text-brand-navy/65">Try a different term, or browse{" "}
            <Link to="/learnings" className="font-semibold text-brand-navy underline decoration-brand-navy/40 hover:decoration-brand-navy/80">all writing</Link>.
          </p>
        </div>
      ) : null}

      {results.length > 0 ? (
        <ul className="mt-4 divide-y divide-brand-navy/10">
          {results.map((a) => (
            <li key={a.id} className="py-6">
              <Link to={`/${a.category}/${a.slug}`} className="group block">
                <span className={`inline-block font-heading text-overline uppercase tracking-[0.14em] px-2 py-0.5 rounded ${categoryAccent(a.category).tag}`}>
                  {categoryLabel(a.category)}
                </span>
                <h2 className="text-xl sm:text-2xl font-bold text-brand-navy mt-2 group-hover:underline decoration-brand-navy/40">{a.title.trim()}</h2>
                {a.description ? <p className="text-brand-navy/70 mt-1 line-clamp-2">{a.description}</p> : null}
                <p className="text-sm text-brand-navy/50 mt-1">
                  {formatUtcShortDate(a.publishedAt)}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}

      {!q ? (
        <p className="mt-8 text-brand-navy/60">
          Or browse by topic on the{" "}
          <Link to="/learnings" className="font-semibold text-brand-navy underline decoration-brand-navy/40 hover:decoration-brand-navy/80">writing index</Link>.
        </p>
      ) : null}
    </div>
  );
}
